import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FeatureToggleService, hd, LoggerService, TicketContextService, TicketService } from '@nx-fe/services';
import IActivityModel = hd.IActivityModel;
import { XsI18nPipe } from '@nx-fe/pipes';
import { ActivityContextService } from 'xsmodules/ticket/components/ticket-details-panel/activity/activity-context';
import { Activity, ActivityTypeEnum, isDeviceSnapshotActivity, SnapshotActivity } from '../../../../ticket.model';
import { take } from 'rxjs/operators';

@Component({
  selector: 'xs-snapshot-activity',
  templateUrl: './snapshot-activity.component.html',
  styleUrls: ['./snapshot-activity.component.scss'],
})
export class SnapshotActivityComponent implements OnInit {
  @Input() snapshot: SnapshotActivity;
  @Input() activityCtrl: ActivityContextService;
  @ViewChild('popupDelete', { static: false }) popupDelete;

  /** Indicates that we are calling that component in Self Service Portal */
  @Input()
  public isSPPMode: boolean;

  @Output()
  isEditting = new EventEmitter<boolean>();

  disableNote = false;
  error: string;

  private noteState: string;
  private editing = false;

  constructor(
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    private ticketService: TicketService,
    private xsI18nPipe: XsI18nPipe,
    private $log: LoggerService
  ) {}

  ngOnInit(): void {
    this.setupCustomIconCallback();
  }

  get canEdit(): boolean {
    if (this.featureToggleService.isEnabled('AbacAllowableActionsGroups')) {
      return this.ticketContextService.canEditNote();
    }
    return false;
  }

  saveNote(event: SnapshotActivity) {
    this.error = '';
    this.disableNote = true;
    const temp = {
      activityId: this.snapshot.activityId,
      activityType: ActivityTypeEnum[this.snapshot.activityTypeId],
      description: event.description,
    };

    // TODO: Remove forced types once types are tidied up
    this.ticketService
      .saveActivity((<unknown>temp) as IActivityModel, this.ticketContextService.ticket.ticketId)
      .subscribe(
        data => {
          const saveResult = <any>data;

          // It's crucial to maintain the reference, as this activity is propogated throughout the code
          Object.assign(this.snapshot, {
            activityId: saveResult.activityId,
            description: event.description,
          });

          this.ticketService
            .getActivity(parseInt(this.ticketContextService.ticket.ticketId, 10), this.snapshot.activityId)
            .subscribe(result => {
              const activity = (<unknown>result) as Activity;
              if (typeof activity.details === 'object' && Object.keys(activity.details).includes('0')) {
                activity.details = activity.details[0];
              }
              if (isDeviceSnapshotActivity(activity) && typeof activity.details.data === 'string') {
                // Snapshot
                activity.details.data = JSON.parse(activity.details.data);
              }

              // crucial to maintain reference, replacing it would break the reference chain
              Object.assign(this.snapshot, activity);

              // Trigger activity setter to force interal user updates (this is the *same* activity)
              this.activityCtrl.Activity = this.snapshot;

              this.disableNote = false;
              this.updateActivityState();
            });
        },
        error => {
          this.error = this.xsI18nPipe.transform('note_Save_Error');
          console.error(error);
          this.disableNote = false;
          this.updateActivityState();
        }
      );
  }

  updateEditing(event) {
    this.editing = event;
    this.isEditting.emit(event);
    this.updateActivityState();
  }

  /** Updates the activity state based on various attributes of the activity, model, and template */
  updateActivityState(): void {
    if (this.error) {
      this.noteState = 'error';
    } else {
      this.noteState = this.editing ? 'active' : 'inactive';
    }
    this.activityCtrl.setActivityState(this.noteState);
  }

  private setupCustomIconCallback() {
    this.snapshot.getCustomIconClass = () => 'hd-icon hd-icon-deviceinfo';
  }

  deleteSnapshot() {
    this.ticketService
      .deleteActivity(this.snapshot.activityId, this.ticketContextService.ticket.ticketId)
      .pipe(take(1))
      .subscribe(
        () => {
          this.$log.debug('Activity deleted');
          this.activityCtrl.delete();
        },
        (error: any) => {
          this.$log.debug('Failed to delete activity');
          this.$log.error(error);
        }
      );
  }

  cancelDelete() {
    this.popupDelete.close();
  }
}
